import coPilotSrc from '@assets/images/co-pilot.png';
import { Button, cn, Markdown, Spinner, Stack } from '@landler/component-library';
import { memo, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Action, Message, MessageType } from '@/api/rest/resources/types/conversation';
import { useOrganizationConversation } from '@/components/CoPilot/hooks/useOrganizationConversations';
import { useUpdateConversation } from '@/components/CoPilot/hooks/useUpdateConversation';
import { withSuspenseBoundary } from '@/components/SuspenseBoundary';
import { Icon } from '@/components/widgets/a/Icon/Icon';
import { isNullish } from '@/utils/booleans';
import { printDDMMYYYYHHMM } from '@/utils/formatting/date';

import { ASK_VA_ACTIONS } from '../constants';
import { ChatMessageAnnotation } from './Annotations';
import { ACTION_BUTTON_STYLE, PromptedActions } from './PromptedActions';

export const ConversationTitle = withSuspenseBoundary(
  ({ conversationId, showCreatedBy = true }: { conversationId: string; showCreatedBy?: boolean }) => {
    const conversation = useOrganizationConversation(conversationId);
    return (
      <Stack className='gap-1 truncate' data-testid='conversation-title'>
        <span className='typography-body1 font-semibold text-text-primary truncate'>{conversation?.title}</span>
        {showCreatedBy && (
          <Stack className='w-full flex-row justify-start items-center gap-4'>
            <span className='typography-caption text-text-secondary'>{conversation?.created_by}</span>
            <span className='typography-caption text-text-secondary'>
              {printDDMMYYYYHHMM(conversation?.modified_at) ?? ''}
            </span>
          </Stack>
        )}
      </Stack>
    );
  },
  <Stack className='justify-center items-center p-4'>
    <Spinner size={4} />
  </Stack>,
);

const AskVaInput = memo(
  ({
    executeActions,
    conversationId,
    isStreamInProgress,
  }: {
    executeActions: (actions: Action[], conversationId?: string) => Promise<void>;
    conversationId: string | null;
    isStreamInProgress: boolean;
  }) => (
    <PromptedActions
      actions={ASK_VA_ACTIONS}
      onExecute={executeActions}
      conversationId={conversationId}
      isStreamInProgress={isStreamInProgress}
      disabled={isStreamInProgress}
    />
  ),
);
AskVaInput.displayName = 'AskVaInput';

export const CurrentChat = ({
  activeAgentType,
  conversationIdArg,
  highlightedMessageId,
}: {
  activeAgentType: string | null;
  conversationIdArg?: string | null;
  highlightedMessageId?: string | null;
}) => {
  const {
    executeActions,
    isStreamInProgress,
    bufferedMessages,
    thinkingMessages,
    streamingContent,
    actions,
    error,
    conversationId,
    pendingUserMessage,
  } = useUpdateConversation(activeAgentType, conversationIdArg ?? null);

  return (
    <Stack data-testid='chat-messages' className='flex flex-col h-full w-full'>
      <div className='flex-1 overflow-y-auto hide-scrollbar min-h-0 flex flex-col gap-6 pb-2 whitespace-pre-wrap text-text-primary typography-body2'>
        {!isNullish(conversationIdArg) && (
          <ChatHistory conversationIdArg={conversationIdArg} highlightedMessageId={highlightedMessageId ?? null} />
        )}
        <CurrentChatMessages
          executeActions={executeActions}
          isStreamInProgress={isStreamInProgress}
          bufferedMessages={bufferedMessages}
          thinkingMessages={thinkingMessages}
          streamingContent={streamingContent}
          actions={actions}
          error={error}
          conversationId={conversationId}
          pendingUserMessage={pendingUserMessage}
        />
      </div>
      <div className='flex-shrink-0 pt-2'>
        <AskVaInput
          executeActions={executeActions}
          conversationId={conversationId}
          isStreamInProgress={isStreamInProgress}
        />
      </div>
    </Stack>
  );
};

const ChatHistory = withSuspenseBoundary(
  ({ conversationIdArg, highlightedMessageId }: { conversationIdArg: string; highlightedMessageId: string | null }) => {
    const conversation = useOrganizationConversation(conversationIdArg);
    const messages: Message[] =
      conversation?.messages?.filter((msg) => msg.context?.type !== MessageType.SUCCESS) || [];
    return (
      <Stack
        key={`chat-history-${conversationIdArg}`}
        data-testid='chat-history'
        className='w-full whitespace-pre-wrap justify-start items-start text-text-primary typography-body2 gap-2 pb-6'
      >
        {messages.length > 0 && (
          <Stack className='gap-3 w-full'>
            {messages.map((message, idx) => {
              const isThinking = message.context?.content_type === 'thinking';
              const isError = message.context?.type === MessageType.ERROR;
              const isHighlighted = highlightedMessageId === message.id;
              return isThinking ? (
                <ChatMessage
                  key={`chat-message-${message.id}-${idx}`}
                  message={message}
                  thinkingMessages={[message.content ?? '']}
                  conversationId={conversationIdArg}
                  isHighlighted={isHighlighted}
                />
              ) : (
                <ChatMessage
                  key={`chat-message-${message.id}-${idx}`}
                  message={message}
                  streamingContent={message.content}
                  actions={message.context?.actions as Action[]}
                  error={isError ? message.content : null}
                  conversationId={conversationIdArg}
                  isHighlighted={isHighlighted}
                />
              );
            })}
          </Stack>
        )}
      </Stack>
    );
  },
  <Stack className='justify-center items-center p-4'>
    <Spinner size={4} />
  </Stack>,
);

const CurrentChatMessages = ({
  executeActions,
  isStreamInProgress,
  bufferedMessages,
  thinkingMessages,
  streamingContent,
  actions,
  error,
  conversationId,
  pendingUserMessage,
}: {
  executeActions: (actions: Action[], conversationId?: string) => void;
  isStreamInProgress: boolean;
  bufferedMessages: Partial<Message>[];
  thinkingMessages: string[];
  streamingContent: string;
  actions: Action[];
  error: string | null;
  conversationId: string | null;
  pendingUserMessage: string | null;
}) => {
  return (
    <Stack
      data-testid='current-chat'
      className='flex flex-col w-full whitespace-pre-wrap justify-start items-start text-text-primary typography-body2 gap-4'
    >
      <Stack className='flex-grow w-full gap-4'>
        {bufferedMessages.slice(0, -2).map((msg, idx) => {
          const isThinking = msg.context?.content_type === 'thinking';
          const isError = msg.context?.type === MessageType.ERROR;
          return (
            <ChatMessage
              key={`chat-message-${msg.id}-${idx}`}
              message={msg as Message}
              thinkingMessages={isThinking ? [msg.content ?? ''] : []}
              streamingContent={isThinking ? null : (msg.content ?? null)}
              actions={(msg.context?.actions as Action[]) || []}
              executeActions={executeActions}
              error={isError ? (msg.content ?? null) : null}
              conversationId={conversationId}
            />
          );
        })}
        {bufferedMessages.slice(-2).map((msg, idx) => {
          const isThinking = msg.context?.content_type === 'thinking';
          const isError = msg.context?.type === MessageType.ERROR;
          return (
            <ChatMessage
              key={`current-chat-message-${msg.id}-${idx}`}
              message={msg as Message}
              thinkingMessages={isThinking ? [msg.content ?? ''] : []}
              streamingContent={isThinking ? null : (msg.content ?? null)}
              actions={(msg.context?.actions as Action[]) || []}
              executeActions={executeActions}
              error={isError ? (msg.content ?? null) : null}
              conversationId={conversationId}
              disabled={isStreamInProgress}
            />
          );
        })}
        {(isStreamInProgress || !isNullish(pendingUserMessage)) && (
          <ChatMessage
            key={`current-chat-message-${conversationId ?? 'new'}`}
            isStreamInProgress={isStreamInProgress}
            thinkingMessages={pendingUserMessage ? [] : thinkingMessages}
            streamingContent={pendingUserMessage ? null : streamingContent}
            actions={pendingUserMessage ? [] : actions}
            executeActions={executeActions}
            error={pendingUserMessage ? null : error}
            conversationId={conversationId}
            disabled={false}
            pendingUserMessage={pendingUserMessage}
          />
        )}
      </Stack>
    </Stack>
  );
};

const ChatMessage = ({
  message,
  isStreamInProgress = false,
  thinkingMessages = [],
  streamingContent = null,
  actions = [],
  executeActions = () => Promise.resolve(),
  error = null,
  conversationId,
  disabled = true,
  isHighlighted = false,
  pendingUserMessage = null,
}: {
  conversationId: string | null;
  executeActions?: (actions: Action[], conversationId?: string) => void;
  thinkingMessages?: string[];
  streamingContent?: string | null;
  actions?: Action[];
  error?: string | null;
  isStreamInProgress?: boolean;
  disabled?: boolean;
  message?: Message;
  isHighlighted?: boolean;
  pendingUserMessage?: string | null;
}) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isHighlighted && ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [isHighlighted]);

  const allowAnnotation =
    !isNullish(message) &&
    !isNullish(message.id) &&
    !message.is_user &&
    !['thinking', 'content'].includes(message.context?.content_type ?? '');

  return (
    <div ref={ref} className={cn('w-full', isHighlighted && 'rounded-lg border-2 border-primary-100 p-2')}>
      <Stack key={message?.id ?? 'current-message'} data-testid='chat-message' className='w-full gap-2'>
        {!isNullish(pendingUserMessage) && (
          <span className='rounded-lg self-end text-text-primary bg-primary-12 px-4 py-2'>
            <Markdown>{pendingUserMessage}</Markdown>
          </span>
        )}

        {(isStreamInProgress || thinkingMessages.length > 0 || pendingUserMessage) && (
          <ChatMessageAnalysis
            thinkingMessages={thinkingMessages}
            isStreamInProgress={isStreamInProgress || !isNullish(pendingUserMessage)}
          />
        )}

        {streamingContent && !error && (
          <span
            className={cn(
              'rounded-lg',
              message?.is_user ? 'self-end text-text-primary bg-primary-12 px-4 py-2' : 'pl-2 self-start',
            )}
          >
            <Markdown>{streamingContent ?? ''}</Markdown>
          </span>
        )}

        {error && <span className='p-4 pl-2 rounded-lg typography-body2 text-error'>{error}</span>}

        {!isNullish(actions) && actions.length > 0 && (
          <PromptedActions
            actions={actions}
            onExecute={executeActions}
            conversationId={conversationId}
            isStreamInProgress={isStreamInProgress}
            disabled={disabled}
          />
        )}

        {allowAnnotation && <ChatMessageAnnotation message={message} />}
      </Stack>
    </div>
  );
};

const ChatMessageAnalysis = ({
  thinkingMessages,
  isStreamInProgress,
}: {
  thinkingMessages: string[];
  isStreamInProgress: boolean;
}) => {
  const { t } = useTranslation();
  const [showThoughts, setShowThoughts] = useState(false);
  return (
    <Stack className='w-full'>
      <Button
        variant='text'
        leftAdornment={
          <img src={coPilotSrc} alt={t('global.copilot.title')} style={{ width: `${18}px`, height: `${18}px` }} />
        }
        rightAdornment={
          thinkingMessages.length > 0 && <Icon icon={showThoughts ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} />
        }
        className={cn(
          ACTION_BUTTON_STYLE,
          'w-fit px-0 text-text-secondary bg-transparent hover:text-text-secondary hover:bg-transparent',
        )}
        onClick={() => setShowThoughts(!showThoughts)}
      >
        {isStreamInProgress && thinkingMessages.length === 0
          ? t('global.copilot.agent.loader')
          : t('global.copilot.agent.analysis')}
      </Button>
      {showThoughts && (
        <Stack className='mb-2 ml-6 p-2 border-l border-neutral-black-12 text-text-secondary'>
          {thinkingMessages.map((thinking, idx) => (
            <Markdown key={idx}>{thinking}</Markdown>
          ))}
        </Stack>
      )}
    </Stack>
  );
};
